import { z } from 'zod';

/* =====================================================================
   Entities. Phase 1 ships companies; the schema is entity-type-agnostic
   by construction — later types add calibration presets, not new shapes.
   ===================================================================== */

export const ENTITY_TYPES = ['company', 'government', 'ngo', 'individual'] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

/** Rollout status per entity type (drives the capability modals). */
export const zCapabilityStatus = z.enum(['ga', 'beta', 'planned']);
export type CapabilityStatus = z.infer<typeof zCapabilityStatus>;

export const LEVELS3 = ['Low', 'Medium', 'High'] as const;
export type Level3 = (typeof LEVELS3)[number];

/** Structured company profile — v1 fields, mirroring the prototype.
    In the full build every field additionally carries provenance
    (manual vs enriched + evidence) at the persistence layer; the engine
    consumes this merged view. */
export const zCompanyProfile = z.object({
  sector: z.string().default('Industrial manufacturing'),
  region: z.string().default('Global'),
  horizon: z.string().default('3–7 years'),
  resourceDep: z.enum(LEVELS3).default('High'),
  footprint: z.enum(LEVELS3).default('High'),
  adaptive: z.enum(LEVELS3).default('Medium'),
  risks: z.array(z.string()).default([]),
});
export type CompanyProfile = z.infer<typeof zCompanyProfile>;

export const zEntity = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(ENTITY_TYPES),
  isSelf: z.boolean().default(false),
  accent: z.string().default('#4f46e5'),
  description: z.string().default(''),
  profile: zCompanyProfile,
});
export type Entity = z.infer<typeof zEntity>;

/** Sector demand-exposure presets: how an entity's demand couples to the
    World3 aggregates (industrial output, services, food, population). */
export interface SectorWeights { ind: number; serv: number; food: number; pop: number; }

export const SECTOR_WEIGHTS: Record<string, SectorWeights> = {
  default:                    { ind: 0.60, serv: 0.25, food: 0.05, pop: 0.10 },
  'Industrial manufacturing': { ind: 0.70, serv: 0.15, food: 0.05, pop: 0.10 },
  'Energy':                   { ind: 0.75, serv: 0.10, food: 0.00, pop: 0.15 },
  'Consumer goods':           { ind: 0.35, serv: 0.20, food: 0.15, pop: 0.30 },
  'Agriculture & food':       { ind: 0.15, serv: 0.10, food: 0.60, pop: 0.15 },
  'Services & software':      { ind: 0.30, serv: 0.55, food: 0.00, pop: 0.15 },
  'Logistics':                { ind: 0.55, serv: 0.25, food: 0.10, pop: 0.10 },
  // entity-type presets beyond companies (M4 beta):
  // a government's "revenue" is its tax base — broad economy + population;
  // NGO funding is pro-cyclical (donations track prosperity) with a
  // services tilt; household income tracks wages across industry+services
  'Public administration':        { ind: 0.30, serv: 0.40, food: 0.05, pop: 0.25 },
  'Environmental advocacy':       { ind: 0.35, serv: 0.40, food: 0.05, pop: 0.20 },
  'Knowledge worker / household': { ind: 0.35, serv: 0.45, food: 0.05, pop: 0.15 },
};

export function sectorWeights(sector: string | undefined): SectorWeights {
  return SECTOR_WEIGHTS[sector ?? ''] ?? SECTOR_WEIGHTS['default'];
}

/** Structured-profile presets per entity type (prototype parity; the M2
    enrichment pipeline replaces these with evidence-based fields). */
export const PROFILE_PRESETS: Record<EntityType, CompanyProfile> = {
  company:    { sector: 'Industrial manufacturing', region: 'Global (HQ Europe)', horizon: '3–7 years',   resourceDep: 'High',   footprint: 'High',   adaptive: 'Medium',
                risks: ['Supply-chain fragility', 'Carbon exposure', 'Energy price shocks'] },
  government: { sector: 'Public administration',    region: 'National',           horizon: '10–30 years', resourceDep: 'Medium', footprint: 'Medium', adaptive: 'Medium',
                risks: ['Food import dependence', 'Fiscal constraints', 'Social cohesion'] },
  ngo:        { sector: 'Environmental advocacy',   region: 'Transnational',      horizon: '10–50 years', resourceDep: 'Low',    footprint: 'Low',    adaptive: 'High',
                risks: ['Funding volatility', 'Political headwinds'] },
  individual: { sector: 'Knowledge worker / household', region: 'Urban',          horizon: '5–20 years',  resourceDep: 'Medium', footprint: 'Medium', adaptive: 'High',
                risks: ['Income concentration', 'Cost-of-living shocks'] },
};

export const ACCENTS = ['#4f46e5', '#0ea5e9', '#e11d48', '#059669', '#d97706', '#7c3aed', '#0891b2'];
